import React, { useState } from 'react'
import {
  MoreVert as MoreVertIcon,
  EditOutlined as EditIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutlined as DeleteIcon,
} from '@mui/icons-material'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import { Menu, MenuItem } from '@resiliatech/birdsong'
import { ChecklistItemWithResourceInfo } from 'TypeDefs/Goal'
import {
  ChecklistItem,
  DeleteRecurringTasksOption,
  Goal,
} from 'Generated/graphql-types'
import DELETE_CHECKLIST_ITEM from 'Apollo/Mutations/DeleteChecklistItem'
import DELETE_RECURRING_TASKS from 'Apollo/Mutations/DeleteRecurringTasks'
import { useMutation } from '@apollo/client'
import { useAppState } from 'Context/AppContext/AppContext'
import { GOALS } from 'Apollo/Queries/Goals'
import {
  ActionsContainer,
  ActionsMenuButton,
  MenuIcon,
} from './TaskItem.styled'
import { TaskDeleteModal } from '../TaskDeleteModal/TaskDeleteModal'
import { taskAnalytics } from '../analytics.utils'
import { useAssigneeEmail } from '../utils'
import { ProgressUpdateDialog } from '../../ProgressUpdate/ProgressUpdateDialog/ProgressUpdateDialog'

type TaskActionsProps = {
  goal: Goal
  onDuplicateTask: (task: ChecklistItemWithResourceInfo) => void
  onEditTask: (task: ChecklistItemWithResourceInfo) => void
  task: ChecklistItemWithResourceInfo
}

export const TaskActions = ({
  goal,
  onDuplicateTask,
  onEditTask,
  task,
}: TaskActionsProps) => {
  const {
    nonprofitOrgConfig: {
      orgId: nonprofitOrgId,
      platformPlan,
      funderOrgId,
      role: userRole,
    },
    userConfig: { id: userId, email: userEmail },
  } = useAppState()
  const [error, setError] = useState('')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isProgressUpdateOpen, setIsProgressUpdateOpen] = useState(false)

  const handleAddUpdate = () => {
    handleCloseMenu()
    setIsProgressUpdateOpen(true)
  }

  const stringId = task.goalId && task.goalId.toString()

  const [deleteChecklistItem] = useMutation(DELETE_CHECKLIST_ITEM, {
    refetchQueries: [
      { query: GOALS, variables: { goalId: stringId, nonprofitOrgId } },
    ],
  })

  const [deleteRecurringTasks] = useMutation(DELETE_RECURRING_TASKS, {
    refetchQueries: [
      { query: GOALS, variables: { goalId: stringId, nonprofitOrgId } },
    ],
  })

  const handleFeedback = (errMsg: string) => setError(errMsg)

  const baseEventProps = {
    assignerEmail: userEmail,
    assignerId: userId,
    funderOrgId,
    goalId: task.goalId,
    goalType: goal.type,
    isGoalSharedWithFunder: !!goal.FunderOrgToSharedGoal,
    nonprofitOrgId,
    platformPlan,
    templateLibraryId: goal.templateLibraryId || null,
    userId,
    userRole,
  }

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => setAnchorEl(null)

  const handleEdit = () => {
    onEditTask(task)
    handleCloseMenu()
  }

  const handleDuplicate = () => {
    onDuplicateTask(task)
    handleCloseMenu()
  }
  const getAssigneeEmail = useAssigneeEmail()

  const handleOpenDeleteModal = () => {
    handleCloseMenu()
    setIsDeleteModalOpen(true)
    if (task.checklistItemGroupId) {
      taskAnalytics.dispatchDeleteRecurringTaskClicked({
        ...baseEventProps,
        assigneeUserEmail: String(getAssigneeEmail(task.owner || null)),
        taskId: task.id,
        taskUpdatedDate: task.updatedAt,
      })
    } else {
      taskAnalytics.dispatchGoalTaskDeleteTaskClicked({
        ...baseEventProps,
        assigneeUserEmail: String(getAssigneeEmail(task.owner || null)),
        taskId: task.id,
        taskUpdatedDate: task.updatedAt,
      })
    }
  }

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false)
    setError('')
  }

  const handleDelete = async (deleteOption: string) => {
    if (task.checklistItemGroupId && deleteOption !== 'single') {
      try {
        const deletedTasks = await deleteRecurringTasks({
          variables: {
            checklistItemId: task.id,
            checklistItemGroupId: task.checklistItemGroupId,
            option:
              deleteOption === 'future'
                ? DeleteRecurringTasksOption.Future
                : DeleteRecurringTasksOption.All,
            nonprofitOrgId,
          },
        })
        deletedTasks.data.deleteRecurringTasks.forEach((t: ChecklistItem) => {
          taskAnalytics.dispatchDeleteRecurringTaskDeleted({
            taskId: t.id,
          })
        })
        taskAnalytics.dispatchDeleteRecurringTaskOptionSaved({
          ...baseEventProps,
          assigneeUserEmail: String(getAssigneeEmail(task.owner || null)),
          taskId: task.id,
          taskUpdatedDate: task.updatedAt,
          deleteOption,
        })
      } catch (e) {
        handleFeedback('Task not found or has no due date')
        return
      }
    } else {
      const deleted = await deleteChecklistItem({
        variables: {
          id: task.id,
          nonprofitOrgId,
        },
      })
      if (deleted.data.deleteChecklistItem) {
        taskAnalytics.dispatchDeleteRecurringTaskDeleted({
          taskId: deleted.data.deleteChecklistItem.id,
        })
      }
    }
    handleCloseDeleteModal()
  }

  return (
    <ActionsContainer>
      <ActionsMenuButton size='small' onClick={handleOpenMenu}>
        <MoreVertIcon />
      </ActionsMenuButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEdit}>
          <MenuIcon component={EditIcon}>
            <EditIcon fontSize='small' />
          </MenuIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <MenuIcon component={ContentCopyIcon}>
            <ContentCopyIcon fontSize='small' />
          </MenuIcon>
          Duplicate
        </MenuItem>
        <MenuItem onClick={handleAddUpdate}>
          <MenuIcon component={ChatBubbleOutlineIcon}>
            <ChatBubbleOutlineIcon fontSize='small' />
          </MenuIcon>
          Add update
        </MenuItem>
        <MenuItem onClick={handleOpenDeleteModal}>
          <MenuIcon component={DeleteIcon}>
            <DeleteIcon fontSize='small' />
          </MenuIcon>
          Delete
        </MenuItem>
      </Menu>
      <TaskDeleteModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        task={task}
        onDelete={handleDelete}
        error={error}
      />
      <ProgressUpdateDialog
        goalId={goal.id}
        open={isProgressUpdateOpen}
        onClose={() => {
          setIsProgressUpdateOpen(false)
        }}
        taskId={task.id}
        showToast
        standalone
      />
    </ActionsContainer>
  )
}
